'use client';

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { useSession } from 'next-auth/react';
import { useQueryClient } from '@tanstack/react-query';
import { connectedAccountsQueryKey } from '@/lib/publish/connectedAccountsClient';

/**
 * B2B — Zendesk Support OAuth: store per-instance client credentials, then connect via Profile strip (platform `zendesk`).
 */
export function ZendeskConnectCard() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const [subdomain, setSubdomain] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  /** True when profile already had subdomain + client id (secret may be omitted on update). */
  const [hasStoredClient, setHasStoredClient] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!session?.user?.id) return;
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetch('/api/user/profile', { credentials: 'include' });
        const j = (await r.json().catch(() => ({}))) as {
          zendeskConnect?: { subdomain?: string; clientId?: string } | null;
        };
        if (cancelled || !r.ok) return;
        const z = j.zendeskConnect;
        if (z?.subdomain) setSubdomain(z.subdomain);
        if (z?.clientId) setClientId(z.clientId);
        if (z?.subdomain && z?.clientId) setHasStoredClient(true);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const save = async () => {
    setErr(null);
    setMsg(null);
    setSaving(true);
    try {
      const r = await fetch('/api/zendesk/connect-settings', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subdomain, clientId, clientSecret }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      if (!r.ok) {
        setErr(j.error || 'Could not save Zendesk settings.');
        return;
      }
      setMsg('Saved. Use the Zendesk bubble in Connected Accounts to finish OAuth.');
      setClientSecret('');
      setHasStoredClient(true);
      void queryClient.invalidateQueries({ queryKey: connectedAccountsQueryKey });
    } catch {
      setErr('Could not save Zendesk settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Zendesk (B2B)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          In Zendesk Admin → Apps and integrations → APIs → OAuth clients, create a client with redirect URI{' '}
          <code>{typeof window !== 'undefined' ? `${window.location.origin}/api/oauth/callback` : '/api/oauth/callback'}</code>
          . Request scope <strong>tickets:read</strong> (or match <code>ZENDESK_OAUTH_SCOPE</code>). Save your subdomain
          and client credentials here, then connect using the Zendesk icon in Connected Accounts.
        </Typography>
        {msg ? (
          <Alert severity="success" sx={{ mb: 1 }} onClose={() => setMsg(null)}>
            {msg}
          </Alert>
        ) : null}
        {err ? (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr(null)}>
            {err}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 480 }}>
          <TextField
            label="Zendesk subdomain"
            size="small"
            value={subdomain}
            onChange={(e) => setSubdomain(e.target.value)}
            placeholder="yourcompany (without .zendesk.com)"
            autoComplete="off"
          />
          <TextField
            label="OAuth unique identifier (client id)"
            size="small"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            autoComplete="off"
          />
          <TextField
            label="Client secret"
            size="small"
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={hasStoredClient ? 'Leave blank to keep existing secret' : 'From Zendesk OAuth client'}
            autoComplete="new-password"
          />
          <Button
            variant="outlined"
            onClick={() => void save()}
            disabled={
              saving || !subdomain.trim() || !clientId.trim() || (!clientSecret.trim() && !hasStoredClient)
            }
          >
            {saving ? 'Saving…' : 'Save Zendesk OAuth app'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
